import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { errorMessage } from "../api/http";
import { isPending, useFollowThread, useMarkThreadRead, useMe, useThread, useUserMap } from "../api/queries";
import type { RoomView } from "../lib/room";
import { Composer } from "./Composer";
import { MessageRow } from "./MessageRow";
import { Icon, Skeleton, useToast } from "./ui";

const GROUP_MS = 5 * 60 * 1000;

export function ThreadPanel({ room, rootId, onClose }: { room: RoomView; rootId: number; onClose: () => void }) {
  const q = useThread(rootId);
  const me = useMe();
  const users = useUserMap();
  const markRead = useMarkThreadRead();
  const follow = useFollowThread(rootId);
  const toast = useToast();
  const scrollRef = useRef<HTMLDivElement>(null);
  const meNames = useMemo(() => new Set([me.username, me.displayName].filter((x): x is string => !!x).map((x) => x.toLowerCase())), [me]);

  const replies = q.data?.replies ?? [];
  const last = replies.at(-1);

  // 새 답글이 오면 아래로
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [last?.id]);

  // 보고 있는 동안 읽음 처리 (따라가는 스레드만 서버가 기록)
  const marked = useRef(0);
  useEffect(() => {
    if (!last || isPending(last) || !q.data?.following || last.id <= marked.current || document.visibilityState !== "visible") return;
    marked.current = last.id;
    markRead.mutate({ rootId, messageId: last.id });
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && !(e.target as HTMLElement).closest("textarea") && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const toggleFollow = () => {
    if (!q.data) return;
    const next = q.data.following && !q.data.muted ? { following: true, muted: true } : { following: true, muted: false };
    follow.mutate(next, { onSuccess: (r) => toast(r.muted ? "이 스레드 알림을 껐습니다." : "이 스레드의 새 답글을 알려드립니다."), onError: (e) => toast(errorMessage(e)) });
  };

  return (
    <aside className="thread-panel" aria-label="스레드">
      <header className="thread-head">
        <div>
          <h2>스레드</h2>
          <span className="sub">{room.title}</span>
        </div>
        {q.data && (
          <button type="button" className="btn btn-sm btn-quiet" onClick={toggleFollow} disabled={follow.isPending}>
            {q.data.following && !q.data.muted ? "알림 끄기" : "알림 받기"}
          </button>
        )}
        <button type="button" className="icon-btn" onClick={onClose} aria-label="스레드 닫기" aria-keyshortcuts="Escape" data-tip="닫기" data-kbd="Esc">
          <Icon name="close" />
        </button>
      </header>
      <div className="thread-scroll" ref={scrollRef} role="log" aria-label="스레드 답글">
        {q.isPending && <Skeleton kind="messages" rows={3} />}
        {q.isError && <div className="load-older">스레드를 열 수 없습니다. {errorMessage(q.error)}</div>}
        {q.data && (
          <>
            <MessageRow message={q.data.root} users={users} me={me} meNames={meNames} continued={false} />
            <div className="day-divider" role="separator">
              {replies.length ? `답글 ${replies.length}개` : "첫 답글을 남겨보세요"}
            </div>
            {replies.map((r, i) => {
              const before = replies[i - 1];
              const continued = !!before && before.authorId === r.authorId && new Date(r.createdAt).getTime() - new Date(before.createdAt).getTime() < GROUP_MS;
              return <MessageRow key={r.id} message={r} users={users} me={me} meNames={meNames} continued={continued} />;
            })}
          </>
        )}
      </div>
      {q.data && !q.data.root.deleted && room.canWrite ? (
        <Composer key={`thread-${rootId}`} room={room} parentId={rootId} placeholder="답글 달기" />
      ) : q.data && !room.canWrite ? (
        <div className="composer-blocked">채널에 참여해야 답글을 달 수 있습니다.</div>
      ) : null}
    </aside>
  );
}
